import type { StandingReplyMessage } from '@hotlist/shared';

/**
 * The authority side of the record. Where Your Week shows a civilian what the
 * network holds on them, this shows an officer what the network's own work has
 * earned them: their rank, their department's standing and what it unlocks, and
 * the operational budget that funds the running of it.
 *
 * The through-line is the game's argument, made playable. You climb on cases
 * closed and clean conduct, never on money; wrongful stops drag both your rank
 * and the department down, and enough of them hold the department's standing
 * where it is no matter how many cases it closes. The budget pays for call-outs
 * and kit and nothing else -- it never buys a rank or a tier.
 */
export class StandingPanel {
  private readonly screen: HTMLElement;

  constructor() {
    this.screen = must('standing-body');
    this.idle();
  }

  /** Before the first report: what the screen is for. */
  private idle(): void {
    this.screen.replaceChildren();
    const empty = document.createElement('div');
    empty.className = 'app-empty';
    const said = document.createElement('p');
    said.textContent =
      'Your rank, your department’s standing, and its budget. Clean stops and closed cases raise them; wrongful stops drag them down.';
    empty.append(said);
    this.screen.append(empty);
  }

  show(r: StandingReplyMessage): void {
    this.screen.replaceChildren();

    // --- The officer's own rank ------------------------------------------
    const rank = document.createElement('div');
    rank.className = 'standing-rank';
    const name = document.createElement('div');
    name.className = 'standing-rank-name';
    name.textContent = r.rank;
    const score = document.createElement('div');
    score.className = 'standing-rank-score';
    score.textContent = `record ${r.score >= 0 ? '+' : ''}${r.score}`;
    rank.append(name, score);
    const nextRank = document.createElement('div');
    nextRank.className = 'standing-next';
    nextRank.textContent = r.nextRank
      ? `${r.toNextRank} to ${r.nextRank}`
      : 'the top of the ladder';
    rank.append(nextRank);
    this.screen.append(rank);

    this.rows([
      ['good stops', r.justified],
      ['wrongful stops', r.wrongful],
      ['pursuits run down', r.pursuits],
    ]);

    // --- The department --------------------------------------------------
    this.section(`${r.agency || 'your department'}`);
    const tier = document.createElement('div');
    tier.className = 'standing-tier';
    tier.textContent = r.tier;
    this.screen.append(tier);

    const tierNext = document.createElement('div');
    tierNext.className = 'standing-next';
    tierNext.textContent = r.heldByConduct
      ? 'held down by wrongful stops'
      : r.nextTier
        ? `${r.toNextTier} cases to ${r.nextTier}`
        : 'top standing';
    if (r.heldByConduct) tierNext.classList.add('standing-warn');
    this.screen.append(tierNext);

    this.rows([
      ['cases closed', r.casesClosed],
      ['units at once', r.dispatchCap],
      ['wrongful rate', `${Math.round(r.wrongfulRatio * 100)}%`],
    ]);

    // --- The budget ------------------------------------------------------
    const budget = document.createElement('div');
    budget.className = 'standing-budget';
    const blabel = document.createElement('span');
    blabel.textContent = 'operational budget';
    const bvalue = document.createElement('span');
    bvalue.className = 'standing-budget-value';
    bvalue.textContent = `$${r.budget.toLocaleString()}`;
    budget.append(blabel, bvalue);
    this.screen.append(budget);

    const note = document.createElement('div');
    note.className = 'standing-note';
    note.textContent = 'Fines and recoveries fund the budget. It pays for call-outs and kit, never for standing.';
    this.screen.append(note);
  }

  private section(title: string): void {
    const head = document.createElement('div');
    head.className = 'standing-head';
    head.textContent = title;
    this.screen.append(head);
  }

  private rows(entries: readonly [string, number | string][]): void {
    for (const [label, value] of entries) {
      const row = document.createElement('div');
      row.className = 'standing-row';
      const name = document.createElement('span');
      name.textContent = label;
      const count = document.createElement('span');
      count.className = 'standing-count';
      count.textContent = typeof value === 'number' ? value.toLocaleString() : value;
      row.append(name, count);
      this.screen.append(row);
    }
  }
}

function must(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing element #${id}`);
  return element;
}
